import { useEffect, useId, useState } from 'react'

import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'

import { useCouponStore } from './coupon'

/**
 * "Have a coupon code?" — a link that opens an input and Apply; once a code
 * is valid it becomes a chip with the server's sentence and a Remove link.
 * `paidPlan`: the user already pays for a plan, and codes only work for new
 * subscriptions, so an applied code is explained instead of being used.
 * `defaultOpen`: the box shows straight away (the Plans page), not behind the link.
 */
export function CouponField({
  paidPlan = false,
  compact = false,
  defaultOpen = false,
  className,
}: {
  paidPlan?: boolean
  compact?: boolean
  defaultOpen?: boolean
  className?: string
}) {
  const { draft, applied, error, checking, setDraft, apply, remove } = useCouponStore()
  const [open, setOpen] = useState(Boolean(defaultOpen || draft || error))
  const inputId = useId()
  const errorId = `${inputId}-error`

  // A code from a link fills the box after the first render: show it.
  useEffect(() => {
    if (draft || error) setOpen(true)
  }, [draft, error])

  if (applied) {
    return (
      <div className={cn('min-w-0', className)}>
        <div
          className={cn(
            'flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1 rounded-field border px-3 py-2',
            paidPlan ? 'border-line bg-surface-muted' : 'border-success/30 bg-success/[0.08]',
          )}
          role="status"
        >
          <TagIcon className={cn('h-4 w-4 shrink-0', paidPlan ? 'text-ink-muted' : 'text-success')} />
          <span className="num rounded-[6px] bg-surface px-1.5 py-0.5 text-[12.5px] font-semibold tracking-wide text-ink shadow-card">{applied.code}</span>
          <span className={cn('min-w-0 flex-1 text-[13px]', paidPlan ? 'text-ink-muted' : 'font-medium text-success')}>{applied.message}</span>
          <button type="button" onClick={remove} className="text-[12.5px] font-medium text-ink-muted underline underline-offset-2 hover:text-ink">
            Remove
          </button>
        </div>
        {paidPlan && <p className="mt-1.5 text-[12.5px] leading-relaxed text-ink-muted">Codes work for new subscriptions, so it does not apply to a change of your current plan.</p>}
        {error && (
          <p role="alert" className="mt-1.5 text-[12.5px] leading-relaxed text-danger">
            {error}
          </p>
        )}
      </div>
    )
  }

  if (!open) {
    return (
      <div className={className}>
        <button type="button" onClick={() => setOpen(true)} className="inline-flex min-h-[36px] items-center gap-1.5 text-[13px] font-medium text-ink-soft underline-offset-2 hover:text-ink hover:underline">
          <TagIcon className="h-4 w-4 text-ink-muted" />
          Have a coupon code?
        </button>
      </div>
    )
  }

  return (
    <form
      className={cn('min-w-0', className)}
      onSubmit={(event) => {
        event.preventDefault()
        void apply()
      }}
      noValidate
    >
      <label htmlFor={inputId} className={cn('mb-1.5 flex items-center gap-1.5 text-[13px] font-semibold text-ink', compact && 'sr-only')}>
        <TagIcon className="h-4 w-4 text-brand" />
        Have a coupon code?
      </label>
      <div className="flex gap-2">
        <input
          id={inputId}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Enter code, e.g. IPHONE-P1"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          maxLength={24}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          className={cn(
            'num h-10 min-w-0 flex-1 rounded-field border bg-surface px-3 text-[14px] uppercase tracking-wide text-ink placeholder:normal-case placeholder:tracking-normal placeholder:text-ink-faint focus:outline-none focus:ring-2',
            error ? 'border-danger/60 focus:ring-danger/15' : 'border-line focus:border-brand/60 focus:ring-brand/15',
          )}
        />
        <Button type="submit" variant="secondary" size="sm" className="h-10 shrink-0" loading={checking} disabled={!draft.trim()}>
          Apply
        </Button>
      </div>
      {error && (
        <p id={errorId} role="alert" className="mt-1.5 text-[12.5px] leading-relaxed text-danger">
          {error}
        </p>
      )}
    </form>
  )
}

export function TagIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M2.5 8.6V3.2c0-.4.3-.7.7-.7h5.4c.2 0 .4.1.5.2l4.5 4.5c.3.3.3.7 0 1l-5.4 5.4c-.3.3-.7.3-1 0L2.7 9.1a.7.7 0 0 1-.2-.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <circle cx="5.6" cy="5.6" r="1.1" fill="currentColor" />
    </svg>
  )
}
